import { SignInIdentifier } from '@logto/schemas';
import i18next from 'i18next';
import type { TFuncKey } from 'i18next';
import { getExampleNumber } from 'libphonenumber-js/mobile';
import examples from 'libphonenumber-js/mobile/examples';
import type { HTMLProps } from 'react';

import { getCountryByCallingCode } from '@/utils/country-code';
import { identifierInputPlaceholderMap } from '@/utils/form';

import type { IdentifierInputType } from './use-smart-input-field';

/**
 * Example-value placeholders shown inside the (always-empty-until-typed) input box.
 * Distinct from `label`, which is a static caption above the box, not inside it — this
 * component has no floating-label behaviour, so on an auto-focused, empty identifier
 * field (SignIn/Register land here first) the box has no visual affordance at all
 * without one of these.
 */
const identifierInputExampleMap: { [K in IdentifierInputType]: TFuncKey } = {
  [SignInIdentifier.Phone]: 'input.phone_number_placeholder',
  [SignInIdentifier.Email]: 'input.email_placeholder',
  [SignInIdentifier.Username]: 'input.username_placeholder',
};

// `i18next.t()` only narrows to `string` when its overload resolution sees a literal key;
// a `TFuncKey`-typed value read out of `identifierInputExampleMap[...]` widens the return
// type to the full translation-resource union (it can, in principle, return a nested
// resource object rather than a leaf string). Every key in the map above is a hardcoded
// leaf string key (verified to resolve in every locale), so the cast is safe — this
// mirrors the same shape `identifierInputPlaceholderMap` in utils/form.ts already has,
// which only avoids the same cast because its one call site immediately stringifies via
// `.join(' / ')`.
const translateExample = (type: IdentifierInputType): string =>
  // `identifierInputExampleMap`'s values are hardcoded leaf-string keys (never a nested
  // resource key), so `i18next.t()` always returns a string here — the wider return type
  // is only because `TFuncKey` itself can't express "leaf key" vs. "namespace key".
  // eslint-disable-next-line no-restricted-syntax
  i18next.t(identifierInputExampleMap[type]) as string;

export const getInputHtmlProps = (
  enabledTypes: IdentifierInputType[],
  currentType?: IdentifierInputType
): Pick<
  HTMLProps<HTMLInputElement>,
  | 'type'
  | 'pattern'
  | 'inputMode'
  | 'placeholder'
  | 'autoComplete'
  | 'label'
  | 'autoCapitalize'
  | 'autoCorrect'
  | 'spellCheck'
> & {
  enterKeyHint?: 'enter' | 'done' | 'go' | 'next' | 'previous' | 'search' | 'send';
} => {
  // No identifier (email / username / phone) should ever be auto-capitalized, autocorrected
  // or spellchecked — on mobile these otherwise capitalize the first letter and add squiggles,
  // forcing the user to fix their input. `enterKeyHint="next"` also gives a clearer keyboard
  // action on the identifier step. Shared across every identifier variant below.
  const commonTextProps = {
    autoCapitalize: 'none',
    autoCorrect: 'off',
    spellCheck: false,
    enterKeyHint: 'next',
  } as const;

  // No `type="email"` and no `pattern`: native constraint validation blocks the form's submit
  // event outright, so the app's own error never renders and the user gets an unstyled browser
  // bubble instead (or, for a pasted "+91 98765-43210", nothing at all). `inputMode` still
  // brings up the right keyboard.
  if (currentType === SignInIdentifier.Phone && enabledTypes.length === 1) {
    return {
      ...commonTextProps,
      type: 'tel',
      inputMode: 'numeric',
      autoComplete: 'tel',
      label: i18next.t('input.phone_number'),
      placeholder: translateExample(SignInIdentifier.Phone),
    };
  }

  if (currentType === SignInIdentifier.Email && enabledTypes.length === 1) {
    return {
      ...commonTextProps,
      type: 'text',
      inputMode: 'email',
      autoComplete: 'email',
      label: i18next.t('input.email'),
      placeholder: translateExample(SignInIdentifier.Email),
    };
  }

  return {
    ...commonTextProps,
    type: 'text',
    // A field-name token list like "email tel" is invalid and disables autofill entirely.
    autoComplete:
      enabledTypes.length > 1
        ? 'username'
        : enabledTypes.map((type) => (type === SignInIdentifier.Phone ? 'tel' : type)).join(''),
    label: enabledTypes.map((type) => i18next.t(identifierInputPlaceholderMap[type])).join(' / '),
    // Multiple identifier types share this one field (e.g. email or username) — a combined
    // placeholder like the label would be cluttered, so show the first enabled type's example,
    // matching how the detection logic below already treats the enabled-type order as priority.
    placeholder: enabledTypes[0] ? translateExample(enabledTypes[0]) : undefined,
  };
};

/**
 * A real mobile number for the selected country, grouped the way it is written there. The
 * national trunk prefix ("07400 123456" in the UK) is dropped: it is never typed after a
 * country code.
 */
export const getPhoneExample = (callingCode: string): string | undefined => {
  const country = getCountryByCallingCode(callingCode);
  const example = country && getExampleNumber(country, examples);

  if (!example) {
    return undefined;
  }

  const formatted = example.formatNational();

  return formatted.startsWith('0') && !example.nationalNumber.startsWith('0')
    ? formatted.slice(1)
    : formatted;
};

/** Digits written with the separators people type or paste: "81234 56789", "(201) 555-0123". */
const formattedPhoneRegex = /^[\d\s()-]*\d[\d\s()-]*$/;

/** Strips phone separators, so type detection and the submitted value see only digits. */
export const stripPhoneFormatting = (value: string) =>
  formattedPhoneRegex.test(value) ? value.replaceAll(/\D/g, '') : value;

const digitsRegex = /^\d*$/;

/** An E.164 number as an app passes it in `login_hint`: "+919876543210". */
const internationalPhoneRegex = /^\+\d{3,}$/;

type DetectIdentifierTypeParams = {
  value: string;
  enabledTypeSet: Set<IdentifierInputType>;
  currentType?: IdentifierInputType;
};

export const detectIdentifierType = ({
  value,
  enabledTypeSet,
  currentType,
}: DetectIdentifierTypeParams) => {
  if (!value && enabledTypeSet.size > 1) {
    /**
     * Multiple types are enabled, so we cannot detect the type without the value.
     * Return `undefined` since the type is not determined.
     */
    return;
  }

  if (enabledTypeSet.size === 1) {
    /**
     * Only one type enabled, so we limit the type to the default type.
     */
    return Array.from(enabledTypeSet)[0];
  }

  const hasAtSymbol = value.includes('@');
  const isAllDigits = digitsRegex.test(value);

  if (
    enabledTypeSet.has(SignInIdentifier.Phone) &&
    ((value.length >= 3 && isAllDigits) || internationalPhoneRegex.test(value))
  ) {
    return SignInIdentifier.Phone;
  }

  if (enabledTypeSet.has(SignInIdentifier.Email) && hasAtSymbol) {
    return SignInIdentifier.Email;
  }

  if (currentType === SignInIdentifier.Phone && isAllDigits) {
    return SignInIdentifier.Phone;
  }

  if (enabledTypeSet.has(SignInIdentifier.Username)) {
    return SignInIdentifier.Username;
  }

  if (enabledTypeSet.has(SignInIdentifier.Email)) {
    return SignInIdentifier.Email;
  }

  return currentType;
};
